/* eslint-disable react/jsx-pascal-case */
import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormHelperText,
  IconButton,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import NavBar from "../../../components/navbar/navbar";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFullScreenButton,
  useMaterialReactTable,
} from "material-react-table";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { Edit, Delete } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { API_URL, sentActionLog, verify } from "../../../utils/common";
import { catalog } from "../../../models";
import swal from "sweetalert";
import { ModalContainer } from "../styles";
import { Error } from "../offers/types";
import { CatalogResponse } from "./types";

const Catalogs: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedCatalog, setSelectedCatalog] = React.useState<string>("");
  const [data, setData] = React.useState<catalog[]>([]);
  const [rowCount, setRowCount] = React.useState<number>(0);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [open, setOpen] = React.useState<boolean>(false);
  const [modalTitle, setModalTitle] = React.useState<string>("");
  const [newValue, setNewValue] = React.useState<string>("");
  const [editId, setEditId] = React.useState<number>(0);
  const [error, setError] = React.useState<Error | undefined>(undefined);
  const columns = React.useMemo<MRT_ColumnDef<catalog>[]>(
    () => [
      {
        accessorKey: "value",
        header: "Id",
        grow: false,
        maxSize: 50,
        size: 50
      },
      {
        accessorKey: "label",
        header: "Nombre",
      },
    ],
    []
  );
  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 20,
  });

  const getAcs = React.useCallback(() => {
    const token = localStorage.getItem("jwt");
    fetch(`${API_URL}/acs`, {
      method: "GET",
      headers: {
        Authorization: `bearer ${token}`,
        "content-type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response: catalog[]) => {
        setIsLoading(false);
        setData(response);
        setRowCount(response.length);
      })
      .catch((err) => {
        console.error(err);
      });
  }, []);

  const getClassifications = () => {
    const token = localStorage.getItem("jwt");
    fetch(`${API_URL}/clasificaciones`, {
      method: "GET",
      headers: {
        Authorization: `bearer ${token}`,
        "content-type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response: catalog[]) => {
        setIsLoading(false);
        setData(response);
        setRowCount(response.length);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const getCompetitors = () => {
    const token = localStorage.getItem("jwt");
    fetch(`${API_URL}/competidores`, {
      method: "GET",
      headers: {
        Authorization: `bearer ${token}`,
        "content-type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response: catalog[]) => {
        setIsLoading(false);
        setData(response);
        setRowCount(response.length);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const getPlays = () => {
    const token = localStorage.getItem("jwt");
    fetch(`${API_URL}/plays`, {
      method: "GET",
      headers: {
        Authorization: `bearer ${token}`,
        "content-type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response: catalog[]) => {
        setIsLoading(false);
        setData(response);
        setRowCount(response.length);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const callCatalog = React.useCallback(() => {
    switch (selectedCatalog) {
      case "acs":
        getAcs();
        break;
      case "clasificaciones":
        getClassifications();
        break;
      case "competidores":
        getCompetitors();
        break;
      case "plays":
        getPlays();
        break;
      default:
        console.warn("Select a Catalog");
    }
  }, [getAcs, selectedCatalog]);

  const validateFields = () => {
    let isValid = true;
    if (newValue === "") {
      setError({
        field: "value",
        message: "El valor no puede estar vacio",
      });
      isValid = false;
    }
    return isValid;
  };

  const create = () => {
    const token = localStorage.getItem("jwt");
    fetch(`${API_URL}/${selectedCatalog}`, {
      method: "POST",
      body: JSON.stringify({ value: newValue }),
      headers: {
        Authorization: `bearer ${token}`,
        "content-type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response: CatalogResponse) => {
        if (response.id) {
          swal({
            title: "Guardado!",
            text: "Se ha guardado correctamente! :)",
            icon: "success",
          });
          setOpen(false);
          setModalTitle("");
          setNewValue("");
          sentActionLog('Create_Row_Catalog', `Creo un nuevo registro con id: ${response.id} en catalogo ${selectedCatalog}`);
          callCatalog();
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const edit = () => {
    const token = localStorage.getItem("jwt");
    fetch(`${API_URL}/${selectedCatalog}/${editId}`, {
      method: "PUT",
      body: JSON.stringify({ value: newValue }),
      headers: {
        Authorization: `bearer ${token}`,
        "content-type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response: CatalogResponse) => {
        if (response.id) {
          swal({
            title: "Guardado!",
            text: "Se ha guardado correctamente! :)",
            icon: "success",
          });
          setOpen(false);
          setModalTitle("");
          setNewValue("");
          setEditId(0);
          callCatalog();
          sentActionLog('Edit_Row_Catalog', `Edito un registro con id: ${response.id} en catalogo ${selectedCatalog}`);
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  React.useEffect(() => {
    callCatalog();
  }, [callCatalog, selectedCatalog]);

  React.useEffect(() => {
    (async () => {
      if (!["/", "/login"].includes(location.pathname)) {
        const currentToken = localStorage.getItem("jwt");
        if (currentToken) {
          const isValid = await verify(currentToken);
          if (!isValid) {
            navigate("/");
          } else {
            const permissions = JSON.parse(
              localStorage.getItem("user") || "{}"
            );
            if (!permissions?.administrationAccess) {
              navigate("/Home");
            }
          }
        } else {
          navigate("/");
        }
      }
    })();
  }, [location.pathname, navigate]);

  React.useEffect(() => {
    sentActionLog('Visit_Catalogs', 'Entro en la vista Catalogos');
  },[]);

  const handleDelete = (row: catalog) => {
    const token = localStorage.getItem("jwt");
    fetch(`${API_URL}/${selectedCatalog}/${row.value}`, {
      method: "DELETE",
      headers: {
        Authorization: `bearer ${token}`,
        "content-type": "application/json",
      },
    })
      .then((res) => res.ok)
      .then((response: boolean) => {
        if (response) {
          swal({
            title: "Eliminado!",
            text: "Se ha eliminado correctamente! :)",
            icon: "success",
          });
          callCatalog();
          sentActionLog('Delete_Row_Catalog', `Elimino un registro con id: ${row.value} en catalogo ${selectedCatalog}`);
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const handleOpen = (title: string) => {
    setModalTitle(title);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setModalTitle("");
    setNewValue("");
  };

  const handleEdit = (row: catalog) => {
    setNewValue(row.label);
    setEditId(row.value);
    handleOpen("Editar");
  };

  const handleSave = () => {
    if (modalTitle) {
      if (modalTitle === "Crear") {
        if (validateFields()) {
          create();
        }
      } else if (modalTitle === "Editar") {
        edit();
      }
    }
  };

  const table = useMaterialReactTable({
    columns,
    data,
    enableFilters: false,
    enableHiding: false,
    enableSorting: false,
    enableColumnActions: false,
    enableRowActions: true,
    manualPagination: true,
    onPaginationChange: setPagination,
    rowCount,
    positionActionsColumn: "last",
    renderToolbarInternalActions: ({ table }) => (
      <>
        <IconButton onClick={() => handleOpen("Crear")}>
          <AddIcon />
        </IconButton>
        <MRT_ToggleDensePaddingButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
      </>
    ),
    renderRowActions: ({ row }) => (
      <Box sx={{ display: "flex", flexWrap: "nowrap", gap: "8px" }}>
        <IconButton color="primary" onClick={() => handleEdit(row.original)}>
          <Edit />
        </IconButton>
        <IconButton color="error" onClick={() => handleDelete(row.original)}>
          <Delete />
        </IconButton>
      </Box>
    ),
    state: {
      isLoading,
      pagination,
    },
    muiBottomToolbarProps: {
      sx: {
        backgroundColor: "#f5f5f5",
      },
    },
    muiTableHeadRowProps: {
      sx: {
        backgroundColor: "#f5f5f5",
      },
    },
    muiTableBodyRowProps: {
      sx: {
        backgroundColor: "#f5f5f5",
      },
    },
    muiTopToolbarProps: {
      sx: {
        backgroundColor: "#f5f5f5",
      },
    },
  });

  return (
    <Box width={"100%"} mb={8}>
      <NavBar />
      <IconButton onClick={() => navigate(-1)}>
        <ArrowBackIcon />
      </IconButton>
      <Card sx={{ minWidth: 275 }}>
        <CardContent>
          <Select
            color="secondary"
            value={selectedCatalog}
            label="Catalogo"
            onChange={(e) => setSelectedCatalog(e.target.value)}
          >
            <MenuItem value="acs">Acs</MenuItem>
            <MenuItem value="clasificaciones">Clasificaciones</MenuItem>
            <MenuItem value="competidores">Competidores</MenuItem>
            <MenuItem value="plays">Plays</MenuItem>
          </Select>
          {selectedCatalog ? (
            <MaterialReactTable table={table} />
          ) : (
            <Alert severity="warning">Por favor selecciona un catalogo</Alert>
          )}
        </CardContent>
      </Card>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="Create-Edit-Offer"
      >
        <ModalContainer theme={theme}>
          <Box display={"flex"} flexDirection="row-reverse">
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box p={4} pt={0} maxHeight={"380px"} overflow={"auto"}>
            <Typography mb={2} variant="h5" fontWeight="bold">
              {modalTitle} Valor
            </Typography>
            <FormControl fullWidth>
              <TextField
                error={error?.field === "zipCode"}
                color="secondary"
                label="Valor"
                variant="outlined"
                value={newValue}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setNewValue(event.target.value);
                }}
              />
              {error?.field === "zipCode" && (
                <FormHelperText>{error.message}</FormHelperText>
              )}
            </FormControl>
            <Button variant="contained" color="secondary" onClick={handleSave}>
              Guardar
            </Button>
          </Box>
        </ModalContainer>
      </Modal>
    </Box>
  );
};

export default Catalogs;
